import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BoltIcon,
  ChartBarIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import IntegrationCard from "./IntegrationCard/IntegrationCard";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import { fetchCompanyProfileAction } from "../../slices/companyProfileSlice";
import { fetchIntegrationsStatusAction } from "../../slices/integrationsStatusSlice";
import ServiceTitanIntegration from "../../../CompanyManagement/components/ServiceTitanIntegration/ServiceTitanIntegration";
import { MembershipSkuSection } from "@/modules/hub/features/MembershipSkuManagement/components/MembershipSkuSection/MembershipSkuSection";
import { editCompany } from "@/api/editCompany/editCompanyApi";
import SwitchIntegrationModal from "./SwitchIntegrationModal/SwitchIntegrationModal";

type Integration = "servicetitan" | "quickbooks_online" | "file_import";

const INTEGRATION_LABELS: Record<Integration, string> = {
  file_import: "File Import",
  servicetitan: "ServiceTitan",
  quickbooks_online: "QuickBooks Online",
};

const Integrations: React.FC = () => {
  const [switchIntegrationTarget, setSwitchIntegrationTarget] =
    useState<Integration | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { companyProfile } = useSelector(
    (state: RootState) => state.companyProfile,
  );
  const { userAppSettings } = useSelector(
    (state: RootState) => state.userAppSettings,
  );
  const { integrationsStatus } = useSelector(
    (state: RootState) => state.integrationsStatus,
  );

  const active = integrationsStatus?.activeIntegration as
    | Integration
    | undefined;
  const [selectedIntegrationCard, setSelectedIntegrationCard] = useState<
    Integration | undefined
  >(active);

  useEffect(() => {
    if (active) {
      setSelectedIntegrationCard(active);
    }
  }, [active]);

  const handleSwitchIntegration = (target: Integration) => {
    if (target === active) return;
    setSwitchIntegrationTarget(target);
  };

  const [isSwitching, setIsSwitching] = useState(false);

  const closeModal = () => {
    setSwitchIntegrationTarget(null);
  };

  const handleConfirmSwitch = async () => {
    if (!switchIntegrationTarget || !companyProfile) return;
    setIsSwitching(true);
    try {
      await editCompany(companyProfile.uuid, {
        activeIntegration: switchIntegrationTarget,
      });
      dispatch(fetchCompanyProfileAction(false));
      dispatch(fetchIntegrationsStatusAction(false));
      setSwitchIntegrationTarget(null);
    } finally {
      setIsSwitching(false);
    }
  };

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
    dispatch(fetchIntegrationsStatusAction());
  }, [dispatch]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrations</CardTitle>
        <CardDescription>
          Choose your field service software and configure the connection.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 tracking-wide mb-3">
          ACTIVE INTEGRATION
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <IntegrationCard
            description="Import data from files without an API connection"
            icon={ArrowUpTrayIcon}
            isActive={active === "file_import"}
            isSelected={selectedIntegrationCard === "file_import"}
            label="File Import"
            onClick={() => setSelectedIntegrationCard("file_import")}
            onSwitch={() => handleSwitchIntegration("file_import")}
            status="no-setup"
          />
          <IntegrationCard
            description="API connection & file import with business unit mapping"
            icon={BoltIcon}
            isActive={active === "servicetitan"}
            isSelected={selectedIntegrationCard === "servicetitan"}
            label="ServiceTitan"
            onClick={() => setSelectedIntegrationCard("servicetitan")}
            onSwitch={() => handleSwitchIntegration("servicetitan")}
            status={
              integrationsStatus?.serviceTitanIntegration?.isConfigured
                ? "configured"
                : "not-configured"
            }
          />
          {/* <IntegrationCard
            description="Invoice sync with club membership SKU detection"
            icon={ChartBarIcon}
            isActive={active === "quickbooks_online"}
            isSelected={selectedIntegrationCard === "quickbooks_online"}
            label="QuickBooks Online"
            onClick={() => setSelectedIntegrationCard("quickbooks_online")}
            onSwitch={() => handleSwitchIntegration("quickbooks_online")}
            status={
              integrationsStatus?.quickBooksOnlineIntegration?.isConfigured
                ? "configured"
                : "not-configured"
            }
          /> */}
        </div>

        {companyProfile && (
          <div className="mt-6">
            {selectedIntegrationCard === "file_import" && (
              <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-5">
                <h3 className="text-sm font-normal text-gray-900 dark:text-white mb-2">
                  File Import
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Upload customer and invoice data directly from CSV or Excel
                  files. Supports ServiceTitan exports and other field service
                  software formats.
                </p>
                <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-gray-300 dark:text-gray-600 mt-0.5">
                      &#8226;
                    </span>
                    No API connection required
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-300 dark:text-gray-600 mt-0.5">
                      &#8226;
                    </span>
                    Accepts CSV, XLSX, and XLS files
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-300 dark:text-gray-600 mt-0.5">
                      &#8226;
                    </span>
                    Import active customers or invoices
                  </li>
                </ul>
              </div>
            )}
            {selectedIntegrationCard === "servicetitan" && (
              <ServiceTitanIntegration
                companyId={userAppSettings?.companyId}
                companyUuid={companyProfile.uuid}
              />
            )}
            {selectedIntegrationCard === "quickbooks_online" && (
              <MembershipSkuSection
                companyId={userAppSettings?.companyId ?? 0}
                companyUuid={companyProfile.uuid}
                currentTenant={userAppSettings?.intacctId}
              />
            )}
          </div>
        )}
      </CardContent>

      <SwitchIntegrationModal
        activeLabel={active ? INTEGRATION_LABELS[active] : ""}
        canSwitchIntegration={integrationsStatus?.canSwitchIntegration ?? false}
        isLoading={isSwitching}
        onClose={closeModal}
        onConfirm={handleConfirmSwitch}
        open={!!switchIntegrationTarget}
        targetLabel={
          switchIntegrationTarget
            ? INTEGRATION_LABELS[switchIntegrationTarget]
            : ""
        }
      />
    </Card>
  );
};

export default Integrations;
